import { getCurrencySymbol } from "../format-utils"

const MODE_FIXED = "fixed-annual"

/**
 * Exports a rate card's trade lines to CSV.
 *
 * @param {object} params
 * @param {object} params.card - Rate card with lines, name, currency
 */
export function exportRateCardCsv({ card }) {
    if (!card) return

    const csv = buildRateCardCsv(card)
    // Prepend UTF-8 BOM so Excel on Windows opens the file as UTF-8 and renders currency / accented characters correctly.
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `${sanitiseFileName(card.name || "Rate Card")}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    setTimeout(() => URL.revokeObjectURL(url), 100)
}

export function buildRateCardCsv(card) {
    const symbol = getCurrencySymbol(card.currency)
    const headers = buildHeaders(symbol)
    const rows = [
        toCardRow(card),
        ...toTradeRows(card.lines || []),
        ...toModifierRows(card.modifiers || [], card.lines),
    ]
    return [headers, ...rows].map(joinRow).join("\n")
}

function buildHeaders(symbol) {
    return [
        "Row Type",
        "Card Name",
        "Description",
        "Currency",
        `Default Rate (${symbol})`,
        "Linked Skill Map",
        "Trade Name",
        "Parent Trade",
        "Skill ID",
        "Rate Type",
        `Hourly Rate (${symbol})`,
        `Fixed Annual (${symbol})`,
        "Contracted Days per Year",
        "Minimum Billing Period (minutes)",
        "Notes",
        "Modifier Order",
        "Modifier Name",
        "Modifier Type",
        "Modifier Scope",
        "Modifier Scope Value",
        "Modifier Value",
        "Compound vs Base",
    ]
}

function toCardRow(card) {
    return makeRow({
        rowType: "Rate Card",
        cardName: card.name,
        description: card.description,
        currency: card.currency || "GBP",
        defaultRate: card.defaultRate,
        skillMap: card.skillMapName || card.skillMapId,
    })
}

function toTradeRows(lines) {
    const rows = []
    for (const line of lines) {
        rows.push(toTradeRow(line))
        for (const override of line.skillOverrides || []) {
            rows.push(toSkillOverrideRow(override, line))
        }
    }
    return rows
}

function toTradeRow(line) {
    const isFixed = line.mode === MODE_FIXED
    return makeRow({
        rowType: "Trade",
        tradeName: line.tradeName || line.tradeId,
        rateType: isFixed ? "Fixed annual" : "Hourly",
        // Leave the column that does not apply to this line's mode blank so the units never mix.
        hourlyRate: isFixed ? "" : line.hourlyRate ?? 0,
        fixedAnnual: isFixed ? line.fixedAnnualAmount ?? 0 : "",
        contractedDaysPerYear: isFixed ? line.contractedDaysPerYear : "",
        minimumBillingPeriod: line.minimumBillingPeriod,
        notes: line.notes,
    })
}

function toSkillOverrideRow(override, line) {
    return makeRow({
        rowType: "Skill Override",
        parentTrade: line.tradeName || line.tradeId,
        skillId: override.skillId,
        hourlyRate: override.hourlyRate,
    })
}

function toModifierRows(modifiers, lines = []) {
    return modifiers.map(toModifierRow)

    function toModifierRow(modifier, index) {
        return makeRow({
            rowType: "Modifier",
            modifierOrder: index + 1,
            modifierName: modifier.name,
            modifierType: formatModifierType(modifier.type),
            modifierScope: formatModifierScope(modifier.scope),
            modifierScopeValue: formatModifierScopeValue(modifier.scope, lines),
            modifierValue: formatModifierValue(modifier),
            modifierBasis: formatModifierBasis(modifier),
        })
    }
}

function makeRow(values) {
    return [
        values.rowType,
        values.cardName,
        values.description,
        values.currency,
        values.defaultRate,
        values.skillMap,
        values.tradeName,
        values.parentTrade,
        values.skillId,
        values.rateType,
        values.hourlyRate,
        values.fixedAnnual,
        values.contractedDaysPerYear,
        values.minimumBillingPeriod,
        values.notes,
        values.modifierOrder,
        values.modifierName,
        values.modifierType,
        values.modifierScope,
        values.modifierScopeValue,
        values.modifierValue,
        values.modifierBasis,
    ].map(escapeCsv)
}

function formatModifierType(type) {
    if (type === "percentage") return "Percentage"
    if (type === "fixed") return "Fixed"
    return type || ""
}

function formatModifierScope(scope) {
    if (!scope || scope.kind === "whole-regime") return "Whole regime"
    if (scope.kind === "trade") return "Trade"
    if (scope.kind === "facility") return "Facility"
    return scope.kind || ""
}

function formatModifierScopeValue(scope, lines) {
    if (!scope?.value) return ""
    if (scope.kind !== "trade") return scope.value
    const trade = findTradeById(lines, scope.value)
    return trade?.tradeName || scope.value
}

function findTradeById(lines, tradeId) {
    for (const line of lines || []) {
        if (line.tradeId === tradeId) return line
    }
    return undefined
}

function formatModifierValue(modifier) {
    if (modifier.type !== "percentage") return modifier.value ?? 0
    const percentage = Number(((modifier.value ?? 0) * 100).toFixed(2))
    return `${percentage}%`
}

function formatModifierBasis(modifier) {
    if (modifier.type !== "percentage") return "Not applicable"
    if (modifier.applyTo === "base") return "Base cost"
    return "Compound"
}

function joinRow(r) {
    return r.join(",")
}

// Block CSV formula injection by prepending a tab when a string starts with a character
// that Excel / LibreOffice would otherwise treat as the start of a formula or command.
const FORMULA_INJECTION_PREFIX = /^[=+\-@|%\t\r]/

function escapeCsv(str) {
    let value = String(str ?? "")
    if (FORMULA_INJECTION_PREFIX.test(value)) {
        value = `\t${value}`
    }
    if (value.includes(",") || value.includes('"') || value.includes("\n") || value.includes("\t")) {
        return `"${value.replace(/"/g, '""')}"`
    }
    return value
}

function sanitiseFileName(name) {
    return name.replace(/[<>:"/\\|?*]/g, "_")
}
